// src/components/NotificationBell.jsx

import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useNotifications } from "../notificationsProvider";
import archiveIcon from "../medias/archive.png";

export const NotificationBell = () => {
  const {
    notifications,
    hasUnread,
    optimisticArchive,
    optimisticMarkRead,
    refreshNotifications,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archive, setArchive] = useState([]);
  const [archiveSearch, setArchiveSearch] = useState("");
  const [animating, setAnimating] = useState({});

  const containerRef = useRef(null);

  // --------------------------------------------------------------
  // English comment: toggle panel
  // --------------------------------------------------------------
  const toggleOpen = () => setIsOpen((prev) => !prev);

  // --------------------------------------------------------------
  // English comment: close when clicking outside
  // --------------------------------------------------------------
  useEffect(() => {
    const h = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (isOpen === true) {
      setShowArchive(false);
    }
  }, [isOpen]);

  // --------------------------------------------------------------
  // English comment: fetch archived notifications
  // --------------------------------------------------------------
  const loadArchive = async () => {
    try {
      const { data } = await axios.post(
        "/api/notification/list-archived",
        {},
        { withCredentials: true }
      );
      setArchive(data.items || []);
    } catch (err) {
      console.error("[NOTIF] loadArchive failed:", err);
    }
  };

  // --------------------------------------------------------------
  // English comment: archive action (optimistic + slide)
  // --------------------------------------------------------------
  const archiveItem = async (id) => {
    setAnimating((prev) => ({ ...prev, [id]: true }));

    setTimeout(() => {
      optimisticArchive(id);
      if (showArchive) {
        loadArchive();
      }
    }, 300);

    try {
      await axios.post(
        "/api/notification/archive",
        { id },
        { withCredentials: true }
      );
    } catch (err) {
      console.error("[NOTIF] archive failed:", err);
      refreshNotifications();
    }
  };

  // --------------------------------------------------------------
  // English comment: mark as read (optimistic)
  // --------------------------------------------------------------
  const markAsRead = async (id) => {
    optimisticMarkRead(id);

    try {
      await axios.post(
        "/api/notification/read",
        { id },
        { withCredentials: true }
      );
    } catch (err) {
      console.error("[NOTIF] markRead failed:", err);
      refreshNotifications();
    }
  };

  // --------------------------------------------------------------
  // English comment: time formatter
  // --------------------------------------------------------------
  const timeAgo = (isoDate) => {
    const delta = Math.floor((Date.now() - new Date(isoDate)) / 1000);
    if (delta < 3600) {
      const mins = Math.floor(delta / 60);
      return `${mins} minute${mins > 1 ? "s" : ""} ago`;
    } else if (delta < 86400) {
      const hrs = Math.floor(delta / 3600);
      return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
    } else {
      const days = Math.floor(delta / 86400);
      return `${days} day${days > 1 ? "s" : ""} ago`;
    }
  };

  // --------------------------------------------------------------
  // English comment: unpack payload safely
  // --------------------------------------------------------------
  const unpack = (notif) => notif.payload || {};

  // --------------------------------------------------------------
  // English comment: render location based on source
  // --------------------------------------------------------------
  const renderLocation = (notif) => {
    const p = unpack(notif);
    if (!p.documentName) return null;

    const isDocumentSource =
      notif.source === "lexical" || notif.source === "document";

    const prefix = isDocumentSource ? "in the document " : "in ";

    return (
      <div className="notif-location">
        {prefix}
        <strong>{p.documentName}</strong>
      </div>
    );
  };

  // --------------------------------------------------------------
  // English comment: archive search
  // --------------------------------------------------------------
  const filteredArchive = useMemo(() => {
    const s = archiveSearch.toLowerCase();
    return archive.filter((n) => {
      const p = unpack(n);
      return (
        (p.message || "").toLowerCase().includes(s) ||
        (p.documentName || "").toLowerCase().includes(s)
      );
    });
  }, [archive, archiveSearch]);

  // --------------------------------------------------------------
  // English comment: render a single notification clickable area
  // --------------------------------------------------------------
  const renderNotifContent = (notif, { closeOnClick }) => {
    const p = unpack(notif);

    const body = (
      <>
        <div className="notif-message">{p.message}</div>
        {renderLocation(notif)}
        <div className="notif-time">{timeAgo(notif.createdAt)}</div>
      </>
    );

    if (p.url) {
      return (
        <a
          href={p.url}
          onClick={() => {
            markAsRead(notif.id);
            if (closeOnClick) setIsOpen(false);
          }}
        >
          {body}
        </a>
      );
    }

    return (
      <div
        className="notif-no-link"
        onClick={() => {
          markAsRead(notif.id);
          if (closeOnClick) setIsOpen(false);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            markAsRead(notif.id);
            if (closeOnClick) setIsOpen(false);
          }
        }}
      >
        {body}
      </div>
    );
  };

  return (
    <div ref={containerRef} className="notification-bell">
      {/* BELL BUTTON */}
      <button
        className={`notification-button ${hasUnread ? "unread" : ""}`}
        onClick={toggleOpen}
        aria-label="Show notifications"
      >
        <svg
          className="notification-icon"
          width="24"
          height="24"
          viewBox="0 0 1000 1000"
        >
          <path d="M893 422L566 93q-27-28-66-28t-66 28L107 422q-27 27-27 66t27 66l248 249q4 4 9 5t10-1 8-6.5 3-10.5V661q0-8-5-13L247 512q-10-10-10-24t10-24l229-231q10-10 24-10t24 10l229 231q10 10 10 24t-10 24L651 614q-5 6-12.5 6t-12.5-6L518 506q-5-5-11.5-2.5T500 513v411q0 7 6.5 9.5T518 931l375-377q27-27 27-66t-27-66z" />
        </svg>

        {hasUnread && <span className="notif-dot"></span>}
      </button>

      {/* PANEL */}
      {isOpen && (
        <div className="notification-tooltip">
          <div className="notification-header">
            <label className="header-title">Notifications</label>
          </div>

          {/* UNREAD + READ */}
          <div className="notification-section">
            {notifications.length === 0 && (
              <div className="notification-empty">Nothing new here!</div>
            )}

            {notifications.map((notif) => {
              const cls =
                notif.status === "unread"
                  ? "notif-item unread"
                  : "notif-item read";

              return (
                <div
                  key={notif.id}
                  className={`${cls} ${animating[notif.id] ? "slide-out" : ""}`}
                >
                  {renderNotifContent(notif, { closeOnClick: true })}

                  {/* ARCHIVE ICON (sprite) */}
                  <div className="notif-archive-wrapper">
                    <div
                      className="notif-archive-icon"
                      onClick={() => archiveItem(notif.id)}
                    ></div>
                    <div className="notif-archive-tooltip">Archive</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ARCHIVE TOGGLE */}
          <div className="archive-toggle">
            <button
              className="archive-button"
              onClick={() => {
                const next = !showArchive;
                setShowArchive(next);
                if (next) loadArchive();
              }}
            >
              {showArchive ? "Hide archived" : "Show archived"}
            </button>
          </div>

          {/* ARCHIVE PANEL */}
          {showArchive && (
            <div className="archive-panel">
              <input
                type="text"
                className="archive-search"
                placeholder="Search archives…"
                value={archiveSearch}
                onChange={(e) => setArchiveSearch(e.target.value)}
              />
              <div className="archive-content-wrapper">
                {filteredArchive.map((notif) => {
                  return (
                    <div key={notif.id} className="notif-item archived">
                      {renderNotifContent(notif, { closeOnClick: true })}
                    </div>
                  );
                })}
              </div>
              {filteredArchive.length === 0 && (
                <div className="notification-empty">No archive match</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};